import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { Music, Camera, Search, Send, Loader2, X, Image as ImageIcon, ChevronUp, User, Heart, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${API}/api`;

export default function GuestExperiencePage() {
  const { eventCode } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [entered, setEntered] = useState(false);
  const [activeTab, setActiveTab] = useState('requests');

  // Song request state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [customArtist, setCustomArtist] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);

  // Photo state
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const fileInputRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    fetchEvent();
  }, [eventCode]);

  useEffect(() => {
    if (!entered) return;
    fetchRequests();
    fetchPhotos();
    const interval = setInterval(() => { fetchRequests(); fetchPhotos(); }, 8000);
    return () => clearInterval(interval);
  }, [entered]);

  const fetchEvent = async () => {
    try {
      const res = await axios.get(`${API_BASE}/event/${eventCode}`);
      setEvent(res.data);
      const savedName = localStorage.getItem(`guest_exp_${eventCode}`);
      if (savedName) {
        setGuestName(savedName);
        const savedEmail = localStorage.getItem(`guest_exp_email_${eventCode}`);
        if (savedEmail) setGuestEmail(savedEmail);
        setEntered(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Event not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/event/${eventCode}/requests`);
      setRequests(res.data);
    } catch (e) { /* silent */ }
  }, [eventCode]);

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/event/${eventCode}/photos`);
      setPhotos(res.data);
    } catch (e) { /* silent */ }
  }, [eventCode]);

  const handleEnter = async () => {
    if (!guestName.trim()) { toast.error('Please enter your name'); return; }
    localStorage.setItem(`guest_exp_${eventCode}`, guestName.trim());
    if (guestEmail.trim()) localStorage.setItem(`guest_exp_email_${eventCode}`, guestEmail.trim());
    // Register guest in background
    try {
      await axios.post(`${API_BASE}/event/${eventCode}/register`, {
        guest_name: guestName.trim(),
        guest_email: guestEmail.trim() || null
      });
    } catch { /* silent - don't block entry */ }
    setEntered(true);
  };

  const handleSearch = (q) => {
    setSearchQuery(q);
    setShowCustom(false);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (q.length < 2) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API_BASE}/event/${eventCode}/search?q=${encodeURIComponent(q)}&limit=10`);
        setSearchResults(res.data);
      } catch (e) { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
  };

  const submitRequest = async (title, artist, inLibrary = true) => {
    if (!title.trim() || !artist.trim()) { toast.error('Song title and artist are required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/event/${eventCode}/request`, {
        guest_name: guestName, song_title: title, artist, note, is_in_library: inLibrary
      });
      toast.success('Song requested!');
      setSearchQuery(''); setSearchResults([]); setNote(''); setCustomTitle(''); setCustomArtist(''); setShowCustom(false);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally { setSubmitting(false); }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('guest_name', guestName);
      try {
        await axios.post(`${API_BASE}/event/${eventCode}/photo`, formData);
        uploaded++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (uploaded > 0) { toast.success(`${uploaded} photo${uploaded > 1 ? 's' : ''} uploaded!`); fetchPhotos(); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Theming
  const pc = event?.primary_color || '#e91e63';
  const ac = event?.accent_color || '#ffc107';
  const bgType = event?.background_type || 'gradient';
  const bgVal = event?.background_value || '#1a1a2e,#16213e';
  const fontClass = event?.font_style === 'modern' ? 'font-sans' : event?.font_style === 'fun' ? 'font-mono' : 'font-serif';

  const bgStyle = bgType === 'gradient'
    ? { background: `linear-gradient(135deg, ${bgVal.split(',')[0]}, ${bgVal.split(',')[1] || bgVal.split(',')[0]})` }
    : bgType === 'solid'
    ? { background: bgVal }
    : { background: '#0f0f1a' };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <Loader2 className="w-8 h-8 animate-spin text-white/50" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="text-center">
        <Music className="w-16 h-16 text-white/20 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Event Not Found</h1>
        <p className="text-white/50">{error}</p>
      </div>
    </div>
  );

  // Entrance screen
  if (!entered) return (
    <div className={`min-h-screen flex flex-col items-center justify-center px-6 ${fontClass}`} style={bgStyle} data-testid="guest-entrance">
      <Toaster position="top-center" theme="dark" />
      <div className="w-full max-w-sm text-center space-y-6">
        {event.couple_photo_url && (
          <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 shadow-2xl" style={{ borderColor: pc }}>
            <img src={event.couple_photo_url.startsWith('/') ? `${API}${event.couple_photo_url}` : event.couple_photo_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight" data-testid="event-title">{event.event_title}</h1>
          <p className="text-white/60 mt-2 text-base">{event.welcome_message}</p>
        </div>
        {event.company_name && (
          <p className="text-xs text-white/30 flex items-center justify-center gap-1">
            <Music className="w-3 h-3" /> {event.company_name}
          </p>
        )}
        <div className="space-y-3 pt-4">
          <Input
            placeholder="Enter your first name"
            value={guestName}
            onChange={e => setGuestName(e.target.value)}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 text-center text-lg h-14 rounded-2xl"
            data-testid="guest-name-input"
          />
          <Input
            placeholder="Email (optional — we'll send you the photos!)"
            type="email"
            value={guestEmail}
            onChange={e => setGuestEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleEnter()}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 text-center text-base h-12 rounded-2xl"
            data-testid="guest-email-input"
          />
          <Button
            onClick={handleEnter}
            className="w-full h-14 text-lg font-semibold rounded-2xl shadow-xl transition-transform active:scale-95"
            style={{ background: pc, color: '#fff' }}
            data-testid="guest-enter-btn"
          >
            Join the Party
          </Button>
        </div>
      </div>
    </div>
  );

  // Main experience
  const pendingRequests = requests.filter(r => r.status === 'pending');
  const playedRequests = requests.filter(r => r.status === 'played');

  return (
    <div className={`min-h-screen ${fontClass} pb-24`} style={bgStyle} data-testid="guest-experience">
      <Toaster position="top-center" theme="dark" />

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/30 border-b border-white/10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {event.couple_photo_url && (
              <div className="w-9 h-9 rounded-full overflow-hidden border-2" style={{ borderColor: pc }}>
                <img src={event.couple_photo_url.startsWith('/') ? `${API}${event.couple_photo_url}` : event.couple_photo_url} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div>
              <h2 className="text-white text-sm font-semibold leading-tight">{event.event_title}</h2>
              <p className="text-white/40 text-xs">{guestName}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="sticky top-[57px] z-30 backdrop-blur-xl bg-black/20 border-b border-white/10">
        <div className="max-w-lg mx-auto flex">
          {event.requests_enabled && (
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 py-3 text-sm font-medium transition-all border-b-2 ${activeTab === 'requests' ? 'text-white' : 'text-white/40 border-transparent'}`}
              style={activeTab === 'requests' ? { borderColor: pc, color: '#fff' } : {}}
              data-testid="tab-requests"
            >
              <Music className="w-4 h-4 inline mr-1.5" />Requests
            </button>
          )}
          {event.photos_enabled && (
            <button
              onClick={() => setActiveTab('photos')}
              className={`flex-1 py-3 text-sm font-medium transition-all border-b-2 ${activeTab === 'photos' ? 'text-white' : 'text-white/40 border-transparent'}`}
              style={activeTab === 'photos' ? { borderColor: pc, color: '#fff' } : {}}
              data-testid="tab-photos"
            >
              <Camera className="w-4 h-4 inline mr-1.5" />Photos {photos.length > 0 && `(${photos.length})`}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* ── Song Requests Tab ── */}
        {activeTab === 'requests' && event.requests_enabled && (
          <>
            {/* Search */}
            <div className="relative" data-testid="song-search-area">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <Input
                placeholder="Search for a song..."
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                className="pl-12 h-14 bg-white/10 border-white/15 text-white placeholder:text-white/30 rounded-2xl text-base"
                data-testid="song-search-input"
              />
              {searching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-white/30" />}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10 overflow-hidden" data-testid="search-results">
                {searchResults.map((song, i) => (
                  <button
                    key={i}
                    onClick={() => !song.is_blocked && submitRequest(song.title, song.artist, true)}
                    disabled={song.is_blocked || submitting}
                    className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-all ${
                      song.is_blocked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/10 active:bg-white/15'
                    } ${i > 0 ? 'border-t border-white/5' : ''}`}
                    data-testid={`search-result-${i}`}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${pc}20` }}>
                      <Music className="w-5 h-5" style={{ color: pc }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{song.title}</p>
                      <p className="text-white/50 text-xs truncate">{song.artist}</p>
                    </div>
                    {song.is_blocked ? (
                      <span className="text-xs text-red-400 shrink-0">Not available</span>
                    ) : (
                      <Send className="w-4 h-4 text-white/30 shrink-0" />
                    )}
                  </button>
                ))}
                <button
                  onClick={() => { setShowCustom(true); setCustomTitle(searchQuery); setSearchResults([]); }}
                  className="w-full px-4 py-3 text-left hover:bg-white/10 transition-all border-t border-white/5"
                  data-testid="cant-find-song-btn"
                >
                  <p className="text-sm" style={{ color: ac }}>Can't find your song? Request it anyway</p>
                </button>
              </div>
            )}

            {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
              <div className="text-center py-6">
                <p className="text-white/40 text-sm mb-2">No songs found for "{searchQuery}"</p>
                <Button
                  variant="outline"
                  onClick={() => { setShowCustom(true); setCustomTitle(searchQuery); }}
                  className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl"
                  data-testid="request-anyway-btn"
                >
                  Request it anyway
                </Button>
              </div>
            )}

            {/* Custom Request Form */}
            {showCustom && (
              <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10 p-4 space-y-3" data-testid="custom-request-form">
                <p className="text-white/60 text-xs">The DJ might not have this song on hand</p>
                <Input
                  placeholder="Song title"
                  value={customTitle}
                  onChange={e => setCustomTitle(e.target.value)}
                  className="bg-white/10 border-white/15 text-white placeholder:text-white/30 rounded-xl"
                  data-testid="custom-title-input"
                />
                <Input
                  placeholder="Artist"
                  value={customArtist}
                  onChange={e => setCustomArtist(e.target.value)}
                  className="bg-white/10 border-white/15 text-white placeholder:text-white/30 rounded-xl"
                  data-testid="custom-artist-input"
                />
                {event.allow_announcements && (
                  <Input
                    placeholder="Add a note (e.g., 'For Sarah's birthday!')"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    className="bg-white/10 border-white/15 text-white placeholder:text-white/30 rounded-xl"
                    data-testid="request-note-input"
                  />
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowCustom(false)}
                    variant="ghost"
                    className="text-white/50 hover:text-white hover:bg-white/10 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => submitRequest(customTitle, customArtist, false)}
                    disabled={submitting}
                    className="flex-1 rounded-xl font-semibold"
                    style={{ background: pc }}
                    data-testid="submit-custom-request-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Request Song'}
                  </Button>
                </div>
              </div>
            )}

            {/* Note for library songs */}
            {!showCustom && searchResults.length === 0 && searchQuery.length === 0 && event.allow_announcements && (
              <div className="bg-white/5 rounded-2xl border border-white/10 p-4" data-testid="note-area">
                <Input
                  placeholder="Add a note to your next request (e.g., 'Happy Birthday Sarah!')"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  className="bg-white/10 border-white/15 text-white placeholder:text-white/30 rounded-xl"
                  data-testid="global-note-input"
                />
              </div>
            )}

            {/* Live Queue */}
            <div className="space-y-3" data-testid="request-queue">
              {pendingRequests.length > 0 && (
                <div>
                  <h3 className="text-white/40 text-xs uppercase tracking-wider mb-2 px-1">Up Next ({pendingRequests.length})</h3>
                  <div className="space-y-2">
                    {pendingRequests.map(r => (
                      <div key={r.id} className="bg-white/5 backdrop-blur rounded-xl p-3 flex items-center gap-3 border border-white/5">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${pc}20` }}>
                          <Music className="w-4 h-4" style={{ color: pc }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{r.song_title}</p>
                          <p className="text-white/40 text-xs truncate">{r.artist} &middot; {r.guest_name}</p>
                          {r.note && <p className="text-xs mt-0.5 truncate" style={{ color: ac }}>{r.note}</p>}
                        </div>
                        {!r.is_in_library && <span className="text-xs text-amber-400/70 shrink-0">Special</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {playedRequests.length > 0 && (
                <div>
                  <h3 className="text-white/40 text-xs uppercase tracking-wider mb-2 px-1">Played ({playedRequests.length})</h3>
                  <div className="space-y-2">
                    {playedRequests.map(r => (
                      <div key={r.id} className="bg-white/5 rounded-xl p-3 flex items-center gap-3 opacity-50">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-green-500/20">
                          <Star className="w-4 h-4 text-green-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{r.song_title}</p>
                          <p className="text-white/40 text-xs truncate">{r.artist}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {requests.length === 0 && (
                <div className="text-center py-12">
                  <Music className="w-12 h-12 text-white/10 mx-auto mb-3" />
                  <p className="text-white/30 text-sm">No requests yet. Be the first!</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Photos Tab ── */}
        {activeTab === 'photos' && event.photos_enabled && (
          <>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full h-14 rounded-2xl text-base font-semibold shadow-lg transition-transform active:scale-95"
              style={{ background: pc }}
              data-testid="upload-photo-btn"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Camera className="w-5 h-5 mr-2" />}
              {uploading ? 'Uploading...' : 'Take / Upload Photo'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={handlePhotoUpload}
              className="hidden"
              data-testid="photo-file-input"
            />

            {/* Photo Grid */}
            {photos.length > 0 ? (
              <div className="grid grid-cols-3 gap-1.5" data-testid="photo-gallery">
                {photos.map(photo => (
                  <button
                    key={photo.id}
                    onClick={() => setViewingPhoto(photo)}
                    className="aspect-square rounded-xl overflow-hidden bg-white/5 border border-white/5 hover:border-white/20 transition-all"
                    data-testid={`photo-${photo.id}`}
                  >
                    <img
                      src={photo.photo_url.startsWith('/') ? `${API}${photo.photo_url}` : photo.photo_url}
                      alt={photo.caption || 'Event photo'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Camera className="w-12 h-12 text-white/10 mx-auto mb-3" />
                <p className="text-white/30 text-sm">No photos yet. Capture the moment!</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Photo Lightbox */}
      {viewingPhoto && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={() => setViewingPhoto(null)}>
          <button className="absolute top-4 right-4 p-2 bg-white/10 rounded-full" onClick={() => setViewingPhoto(null)}>
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={viewingPhoto.photo_url.startsWith('/') ? `${API}${viewingPhoto.photo_url}` : viewingPhoto.photo_url}
            alt=""
            className="max-w-full max-h-[85vh] rounded-lg"
            onClick={e => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-0 right-0 text-center">
            <p className="text-white/60 text-sm">{viewingPhoto.guest_name}</p>
            {viewingPhoto.caption && <p className="text-white/40 text-xs">{viewingPhoto.caption}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
